// NOTE: You must have react-easy-crop and a helper util getCroppedImg implemented.

import * as React from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/GridLegacy";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import VerifiedIcon from "@mui/icons-material/Verified";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkIcon from "@mui/icons-material/Link";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import { useQuery, useMutation } from "@apollo/client";
import { User as Auth0User, useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import {
  GET_USER_PROFILE_BY_ID,
  GET_USER_COLLECTIONS,
} from "../../data/graphql/queries";
import { DELETE_USER_ACCOUNT } from "../../data/graphql/mutations";
import InkBowlPlusModal from "../../components/InkBowlPlusModal";
import {
  UserType,
  BadgeType,
  PostType,
  PublicationType,
  TagType,
  CollectionType,
} from "../../data/types";
import en from "../../data/en.json";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop";
import getCroppedImg from "../../lib/cron-image";

interface MyProfileProps {
  user: Auth0User;
}

export default function MyProfile(props: MyProfileProps) {
  const { logout } = useAuth0();
  const { loading, error, data, refetch } = useQuery(GET_USER_PROFILE_BY_ID, {
    variables: { user_auth0_id: props.user.sub },
    errorPolicy: "all",
    onError: (error) => {
      console.error("GraphQL Error in MyProfile dashboard:", {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        extraInfo: error.extraInfo,
        userAuth0Id: props.user.sub,
      });
    },
    onCompleted: (data) => {
      console.log("MyProfile dashboard data loaded successfully:", data);
    },
  });

  const [tab, setTab] = React.useState<string>("1");
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const {
    loading: collectionsLoading,
    error: collectionsError,
    data: collectionsData,
  } = useQuery(GET_USER_COLLECTIONS, {
    variables: {
      user_auth0_id: props.user.sub,
      include_private: true,
    },
  });

  const [showEditProfile, setShowEditProfile] = React.useState(false);
  const [user, setUser] = React.useState<UserType | null>(null);
  const [handle, setHandle] = React.useState("");
  const [pronouns, setPronouns] = React.useState("");
  const [pictureUrl, setPictureUrl] = React.useState("");
  const [twitter, setTwitter] = React.useState("");
  const [instagram, setInstagram] = React.useState("");
  const [bio, setBio] = React.useState("");
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false);
  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [showInkbowlPlusModal, setShowInkbowlPlusModal] = React.useState(false);

  const [file, setFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [uploadError, setUploadError] = React.useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(
    null
  );
  const [croppingModalOpen, setCroppingModalOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteUserAccount, { loading: deleteLoading }] = useMutation(DELETE_USER_ACCOUNT, {
    onCompleted: () => {
      logout({ logoutParams: { returnTo: window.location.origin } });
    },
    onError: () => {
      setShowErrorAlert(true);
    },
  });

  React.useEffect(() => {
    console.log("MyProfile useEffect triggered with data:", data);
    if (data && data.userProfileById) {
      const u = data.userProfileById as UserType;
      console.log("Setting MyProfile user data:", {
        userId: u.id,
        handle: u.handle,
        hasBadges: !!u.badges,
        badgesCount: u.badges?.length || 0,
        profile: u.profile,
      });
      setUser(u);
      setHandle(u.handle || "");
      setPronouns(u.profile?.pronouns || "");
      setPictureUrl(u.profilePhotoUrl ?? "");
      setTwitter(u.profile?.twitter ?? "");
      setInstagram(u.profile?.instagram ?? "");
      setBio(u.profile?.bio ?? "");
    } else if (data) {
      console.warn("MyProfile data is null for user:", props.user.sub);
    }
  }, [data, props.user.sub]);

  const handleUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(selectedFile.type)) {
      setUploadError("Only PNG, JPG, or JPEG files are allowed.");
      return;
    }
    if (selectedFile.size > maxSize) {
      setUploadError("File must be smaller than 5MB.");
      return;
    }

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result as string);
      setCroppingModalOpen(true);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleProfileUpload = async () => {
    if (!file || !imagePreview || !croppedAreaPixels) return;
    const croppedBlob = await getCroppedImg(imagePreview, croppedAreaPixels);
    const croppedFile = new File([croppedBlob], file.name, {
      type: "image/jpeg",
    });

    const formData = new FormData();
    formData.append(
      "operations",
      JSON.stringify({
        query: `
          mutation updateUserProfile(
            $user_auth0_id: String!,
            $handle: String!,
            $pronouns: String,
            $profile_photo: Upload,
            $twitter: String,
            $instagram: String,
            $bio: String,
          ) {
            updateUserProfile(
              userAuth0Id: $user_auth0_id,
              handle: $handle,
              pronouns: $pronouns,
              profilePhoto: $profile_photo,
              twitter: $twitter,
              instagram: $instagram,
              bio: $bio
            ) { id email role auth0Id handle verified profilePhotoUrl }
          }
        `,
        variables: {
          user_auth0_id: props.user.sub,
          handle,
          pronouns,
          twitter,
          instagram,
          bio,
          profile_photo: null,
        },
      })
    );

    formData.append(
      "map",
      JSON.stringify({ "0": ["variables.profile_photo"] })
    );
    formData.append("0", croppedFile);

    const token = process.env.REACT_APP_INKBOWL_API_TOKEN;
    const res = await fetch(
      `${process.env.REACT_APP_INKBOWL_API_HOST}/graphql`,
      {
        method: "POST",
        body: formData,
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    const json = await res.json();
    if (json.errors || json.data.updateUserProfile.errors?.length) {
      setShowErrorAlert(true);
    } else {
      setShowSuccessAlert(true);
      setPictureUrl(json.data.updateUserProfile.profilePhotoUrl);
      setCroppingModalOpen(false);
      refetch();
    }
  };

  const renderPosts = (posts: PostType[]) => {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
          gap: "1rem",
        }}
      >
        {posts.map((post: PostType, index: number) => (
          <Box key={index}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Avatar src={user?.profile?.photo?.url} />
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", pr: 1, pl: 1 }}
              >
                {user?.profile?.nickname}
              </Typography>
              <Typography variant="body1" sx={{ pr: 1 }}>
                {post.createdAt}
              </Typography>
            </Box>
            <Box
              sx={{
                maxHeight: 250,
                pt: 1,
                pb: 1,
                overflow: "hidden",
                overflowY: "scroll",
              }}
            >
              <Typography variant="body2">{post.text}</Typography>
            </Box>
            {post.imageUrl && (
              <img
                alt={en.community.postImage}
                src={post.imageUrl}
                width={"100%"}
                height={"auto"}
              />
            )}
          </Box>
        ))}
      </div>
    );
  };

  const renderPublications = (publications: PublicationType[]) => {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
          gap: "1rem",
        }}
      >
        {publications.map((publication: PublicationType) => (
          <Link
            key={publication.uuid}
            to={`/publications/${publication.uuid}`}
            style={{ textDecoration: "none", color: "black" }}
          >
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                height: "auto",
                minHeight: 200,
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "action.hover",
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {publication.title}
              </Typography>
              {publication.headline && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 2,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {publication.headline}
                </Typography>
              )}
              <Box sx={{ mt: "auto", pt: 1 }}>
                <Box
                  sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}
                >
                  {publication.tags
                    .slice(0, 3)
                    .map((tag: TagType, index: number) => (
                      <Chip
                        key={index}
                        label={tag.name}
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    ))}
                  {publication.tags.length > 3 && (
                    <Chip
                      label={`+${publication.tags.length - 3} more`}
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="caption" sx={{
                    color: "text.secondary",
                  }}
                >
                    Status: {publication.status}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Link>
        ))}
      </div>
    );
  };

  const renderGalleries = (collections: CollectionType[]) => {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "1rem",
        }}
      >
        {collections.map((collection: CollectionType) => (
          <Link
            key={collection.id}
            to={`/collections/${collection.uuid}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "action.hover",
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                {collection.title}
              </Typography>
              {collection.description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 1,
                  }}
                >
                  {collection.description}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{
                  color: "text.secondary",
                }}
              >
                  {collection.itemsCount} items
                </Typography>
                <Chip
                  label={collection.public ? "Public" : "Private"}
                  size="small"
                  variant={collection.public ? "outlined" : "filled"}
                  color={collection.public ? "primary" : "default"}
                />
              </Box>
            </Box>
          </Link>
        ))}
      </div>
    );
  };

  const renderBadges = (badges: BadgeType[]) => {
    if (!badges || badges.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          pt: 0.5,
          pb: 1,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {badges.map((badge: BadgeType) => (
          <Chip
            key={badge.id}
            label={badge.name}
            variant="outlined"
            size="medium"
            avatar={
              badge.imageUrl ? (
                <Avatar
                  src={badge.imageUrl}
                  sx={{
                    width: 28,
                    height: 28,
                    marginLeft: "8px !important",
                    marginRight: "-4px !important",
                  }}
                />
              ) : undefined
            }
            sx={{
              backgroundColor: "white",
              color: "#2B2D6A",
              borderColor: "#2B2D6A",
              borderWidth: "2px",
              fontWeight: "bold",
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
              height: "36px",
              minHeight: "36px",
              maxHeight: "36px",
              borderRadius: "18px",
              boxShadow: "0 2px 4px rgba(43, 45, 106, 0.1)",
              transition: "all 0.2s ease-in-out",
              "& .MuiChip-label": {
                paddingLeft: "12px",
                paddingRight: "12px",
                lineHeight: "1.2",
                fontWeight: "600",
              },
              "& .MuiChip-avatar": {
                width: "28px",
                height: "28px",
                marginLeft: "8px",
                marginRight: "-4px",
              },
              "&:hover": {
                backgroundColor: "#f8f9ff",
                borderColor: "#2B2D6A",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(43, 45, 106, 0.15)",
              },
            }}
          />
        ))}
      </Box>
    );
  };

  // Add logging for current state
  console.log("MyProfile render state:", {
    loading,
    error: !!error,
    user: !!user,
    data: !!data,
  });

  if (loading) {
    console.log("MyProfile is loading...");
    return <Typography>{en.common["loading.."]}</Typography>;
  }

  if (error) {
    console.error("MyProfile error:", error);
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Dashboard Error: Unable to load profile
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Error Message: {error.message}
        </Typography>
        {error.graphQLErrors?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">GraphQL Errors:</Typography>
            {error.graphQLErrors.map((err, index) => (
              <Typography key={index} variant="body2" sx={{
                color: "text.secondary",
              }}
            >
                • {err.message}
              </Typography>
            ))}
          </Box>
        )}
        {error.networkError && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Network Error:</Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary",
            }}
          >
              {error.networkError.message}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" sx={{
          color: "text.secondary",
        }}
      >
          User Auth0 ID: {props.user.sub}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!user) {
    console.warn("MyProfile: No user data available");
    return <Typography>No user profile data available</Typography>;
  }

  return (
    <>
      <InkBowlPlusModal
        open={showInkbowlPlusModal}
        handleClose={() => setShowInkbowlPlusModal(false)}
      />
      {showSuccessAlert && (
        <Alert severity="success" onClose={() => setShowSuccessAlert(false)}>
          {en.userDashboard.myProfileSection.updateProfileSuccess}
        </Alert>
      )}
      {showErrorAlert && (
        <Alert severity="error" onClose={() => setShowErrorAlert(false)}>
          {en.userDashboard.myProfileSection.updateProfileFailure}
        </Alert>
      )}

      {showEditProfile && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={3} />
          <Grid item xs={6} spacing={2}>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <TextField
                label={en.userDashboard.myProfileSection.handle}
                required
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <TextField
                label={en.userDashboard.myProfileSection.pronouns}
                value={pronouns}
                onChange={(e) => setPronouns(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <TextField
                label={en.common.twitter}
                value={twitter}
                onChange={(e) => setTwitter(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <TextField
                label={en.common.instagram}
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <TextField
                label={en.common.bio}
                value={bio}
                multiline
                fullWidth
                rows={4}
                onChange={(e) => setBio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {en.userDashboard.myProfileSection.uploadProfilePicture}
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={fileInputRef}
                  onChange={handleUploadFileChange}
                />
                {pictureUrl && (
                  <img
                    src={pictureUrl}
                    alt="profile"
                    width={100}
                    style={{ borderRadius: 8 }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button size="large" onClick={() => setShowEditProfile(false)}>
                {en.common.cancel}
              </Button>
              <Button
                sx={{ ml: 2 }}
                variant="contained"
                size="large"
                onClick={handleProfileUpload}
              >
                {en.userDashboard.myProfileSection.updateProfile}
              </Button>
            </Grid>
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete My Account
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!showEditProfile && user && (
        <Grid
          container
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
          sx={{ mt: 10, pt: 5 }}
        >
          <Grid item xs={2} />
          <Grid item xs={8} sx={{ width: 750 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{ mr: 1 }}
                alt="userProfile"
                src={user.profile.photo?.url}
              />
              <Typography variant="h5" sx={{ mr: 1 }}>
                {user.profile.nickname}
              </Typography>
              {user.profile.pronouns && (
                <Typography variant="h5" sx={{ mr: 1 }}>
                  ({user.profile.pronouns})
                </Typography>
              )}
              <Typography variant="h5" sx={{ mr: 1 }}>
                @{user.handle}
              </Typography>
              {(user.verified || true) && <VerifiedIcon />}
              {user.isPremiumUser && <WorkspacePremiumIcon />}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                pt: 2,
                pb: 0,
              }}
            >
              {user.profile.twitter && (
                <Button
                  component={Link}
                  to={user.profile.twitter}
                  variant="text"
                >
                  <TwitterIcon />
                </Button>
              )}
              {user.profile.instagram && (
                <Button
                  component={Link}
                  to={user.profile.instagram}
                  variant="text"
                >
                  <InstagramIcon />
                </Button>
              )}
              <Button
                component={Link}
                to={`/users/${handle}`}
                variant="text"
                startIcon={<LinkIcon />}
              >
                View public profile
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                pt: 2,
                pb: 0,
                m: 2,
              }}
            >
              {user.profile?.bio ? (
                <Typography variant="body1">{user.profile.bio}</Typography>
              ) : (
                <Typography variant="body1">{en.common.noBio}</Typography>
              )}
            </Box>

            {/* Badges Section */}
            <Box sx={{ m: 2 }}>
              {(() => {
                console.log("MyProfile badges render check:", {
                  hasBadges: !!user.badges,
                  badgesLength: user.badges?.length,
                  badges: user.badges,
                });
                try {
                  return user.badges && renderBadges(user.badges);
                } catch (badgeError) {
                  console.error(
                    "Badge rendering error in MyProfile:",
                    badgeError
                  );
                  return (
                    <Typography variant="body2" sx={{
                      color: "text.secondary"
                    }}>Badges temporarily unavailable
                                          </Typography>
                  );
                }
              })()}
            </Box>

            <Grid item xs={12} sx={{ m: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowEditProfile(true)}
              >
                {en.userDashboard.myProfileSection.editProfile}
              </Button>
            </Grid>
            {!user.isPremiumUser && (
              <Grid item xs={12} sx={{ m: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => setShowInkbowlPlusModal(true)}
                >
                  {en.subscription.signUpForInkBowlPlus}
                </Button>
              </Grid>
            )}

            <Box sx={{ width: "100%", typography: "body1", mt: 2 }}>
              <TabContext value={tab}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList onChange={handleTabChange}>
                    <Tab label={en.common.posts} value="1" />
                    <Tab label={en.common.publications} value="2" />
                    <Tab label={en.common.galleries} value="3" />
                  </TabList>
                </Box>
                <TabPanel value="1">
                  {user.posts.length === 0 && en.userProfile.noPosts}
                  {user.posts.length > 0 && renderPosts(user.posts)}
                </TabPanel>
                <TabPanel value="2">
                  {user.publications.length === 0 &&
                    en.userProfile.noPublications}
                  {user.publications.length > 0 &&
                    renderPublications(user.publications)}
                </TabPanel>
                <TabPanel value="3">
                  {collectionsLoading && <p>{en.common["loading.."]}</p>}
                  {collectionsError && (
                    <p>
                      {en.common.error}: {collectionsError.message}
                    </p>
                  )}
                  {!collectionsLoading && !collectionsError && (
                    <>
                      {(!collectionsData?.userCollections ||
                        collectionsData.userCollections.length === 0) &&
                        "No galleries yet"}
                      {collectionsData?.userCollections &&
                        collectionsData.userCollections.length > 0 &&
                        renderGalleries(collectionsData.userCollections)}
                    </>
                  )}
                </TabPanel>
              </TabContext>
            </Box>
          </Grid>
        </Grid>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ px: 4, pt: 4 }}>Delete Account</DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          <Typography>
            You are about to <strong>PERMANENTLY delete</strong> all your user data,
            publications, and posts. This action cannot be undone. Are you sure you
            want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteLoading}
            onClick={() =>
              deleteUserAccount({ variables: { user_auth0_id: props.user.sub } })
            }
          >
            {deleteLoading ? "Deleting..." : "Yes, Delete My Account"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={croppingModalOpen}
        onClose={() => setCroppingModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Crop Profile Image</DialogTitle>
        <DialogContent>
          {imagePreview && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 300,
              }}
            >
              <Cropper
                image={imagePreview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedArea) =>
                  setCroppedAreaPixels(croppedArea)
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCroppingModalOpen(false)}>
            {en.common.cancel}
          </Button>
          <Button variant="contained" onClick={handleProfileUpload}>
            {en.userDashboard.myProfileSection.updateProfile}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
